
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface CartItem {
  id: string;
  productId: string;
  variantId?: string;
  quantity: number;
  attributes?: {[key: string]: string};
  // Fields populated from backend validation (not persisted)
  name?: string;
  price?: number;
  image?: string;
  maxStock?: number;
  available?: boolean;
  // Legacy fields for backward compatibility
  color?: string;
  size?: string;
}

interface CartState {
  items: CartItem[];
  isOpen: boolean;
  appliedCoupon: string | null;
  discountPercentage: number;
}

interface CartStore {
  state: CartState;
  addItem: (productId: string, quantity: number, attributes?: {[key: string]: string}, meta?: Partial<CartItem>) => void;
  removeItem: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  bulkUpdateItems: (updates: {id: string, quantity: number}[]) => void;
  clearCart: () => void;
  setIsOpen: (isOpen: boolean) => void;
  applyCoupon: (code: string) => void;
  removeCoupon: () => void;
  getTotalItems: () => number;
  getItems: () => CartItem[];
  // Deprecated methods - kept for backward compatibility
  // These should be replaced with backend API calls
  getSubtotal: () => number;
  getDiscount: () => number;
  getTotal: () => number;
}

const initialState: CartState = {
  items: [],
  isOpen: false,
  appliedCoupon: null,
  discountPercentage: 0,
};

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      state: initialState,

      addItem: (productId, quantity, attributes, meta) => {
        const state = get().state;
        
        // Check if item with same productId, variantId and attributes already exists
        const existingItemIndex = state.items.findIndex(
          item => {
            if (item.productId !== productId) return false;
            // Match variantId if present
            if (meta?.variantId || item.variantId) {
              if ((item.variantId || '') !== (meta?.variantId || '')) return false;
            }
            
            // Compare attributes if they exist
            if (!attributes && !item.attributes) return true;
            if (!attributes || !item.attributes) return false;
            
            const itemAttrsStr = JSON.stringify(item.attributes);
            const newAttrsStr = JSON.stringify(attributes);
            return itemAttrsStr === newAttrsStr;
          }
        );

        if (existingItemIndex > -1) {
          // Update existing item quantity
          const updatedItems = [...state.items];
          updatedItems[existingItemIndex] = {
            ...updatedItems[existingItemIndex],
            quantity: updatedItems[existingItemIndex].quantity + quantity
          };

          set({
            state: {
              ...state,
              items: updatedItems
            }
          });
        } else {
          // Add new item with optional metadata for immediate UX
          const cartItem: CartItem = {
            id: `${productId}-${Date.now()}`,
            productId,
            variantId: meta?.variantId,
            quantity,
            attributes,
            name: meta?.name,
            price: meta?.price,
            image: meta?.image,
            maxStock: meta?.maxStock,
          };

          set({
            state: {
              ...state,
              items: [...state.items, cartItem]
            }
          });
        }
      },

      removeItem: (itemId) => {
        const state = get().state;
        set({
          state: {
            ...state,
            items: state.items.filter(item => item.id !== itemId)
          }
        });
      },

      updateQuantity: (itemId, quantity) => {
        const state = get().state;
        if (quantity <= 0) {
          get().removeItem(itemId);
          return;
        }

        const updatedItems = state.items.map(item => {
          if (item.id === itemId) {
            return {
              ...item,
              quantity
            };
          }
          return item;
        });

        set({
          state: {
            ...state,
            items: updatedItems
          }
        });
      },
      
      bulkUpdateItems: (updates) => {
        const state = get().state;
        const updatedItems = state.items.map(item => {
          const update = updates.find(u => u.id === item.id);
          if (update) {
            return {
              ...item,
              quantity: update.quantity
            };
          }
          return item;
        });

        set({
          state: {
            ...state,
            items: updatedItems
          }
        });
      },

      clearCart: () => {
        set({
          state: {
            ...initialState
          }
        });
      },

      setIsOpen: (isOpen) => {
        const state = get().state;
        set({
          state: {
            ...state,
            isOpen
          }
        });
      },

      applyCoupon: (code) => {
        const state = get().state;
        // Simple coupon logic - can be enhanced later
        const discountPercentage = code.toLowerCase() === 'descuento10' ? 10 : 0;
        
        set({
          state: {
            ...state,
            appliedCoupon: discountPercentage > 0 ? code : null,
            discountPercentage
          }
        });
      },

      removeCoupon: () => {
        const state = get().state;
        set({
          state: {
            ...state,
            appliedCoupon: null,
            discountPercentage: 0
          }
        });
      },

      getTotalItems: () => {
        const state = get().state;
        return state.items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
      },

      getItems: () => {
        const state = get().state;
        return state.items;
      },

      // Deprecated methods - return 0 as placeholder
      // TODO: Replace with backend API calls in ShoppingCart component
      getSubtotal: () => {
        console.warn('getSubtotal() is deprecated. Use backend API to get cart totals.');
        return 0;
      },

      getDiscount: () => {
        return 0; // Discount should come from backend
      },

      getTotal: () => {
        console.warn('getTotal() is deprecated. Use backend API to get cart totals.');
        return 0;
      },
    }),
    {
      name: 'cart-storage',
    }
  )
);
